import { useState, useEffect, useCallback } from 'react';
import { userService, UserProfile, UserPreferences, UpdateProfileData, ChangePasswordData, TwoFactorData } from '../services/userService';
import { ApiError } from '../services/api';

interface UseUserSettingsReturn {
  // Profile data
  profile: UserProfile | null;
  preferences: UserPreferences | null;
  
  // Loading states
  profileLoading: boolean;
  preferencesLoading: boolean;
  
  // Error states
  profileError: string | null;
  preferencesError: string | null;
  
  // Actions
  updateProfile: (data: UpdateProfileData) => Promise<void>;
  changePassword: (data: ChangePasswordData) => Promise<void>;
  updatePreferences: (preferences: Partial<UserPreferences>) => Promise<void>;
  toggleTwoFactor: (data: TwoFactorData) => Promise<void>;
  refreshProfile: () => Promise<void>;
  refreshPreferences: () => Promise<void>;
  
  // Success states
  profileUpdateSuccess: boolean;
  passwordChangeSuccess: boolean;
  preferencesUpdateSuccess: boolean;
  twoFactorUpdateSuccess: boolean;
  
  // Clear success states
  clearProfileUpdateSuccess: () => void;
  clearPasswordChangeSuccess: () => void;
  clearPreferencesUpdateSuccess: () => void;
  clearTwoFactorUpdateSuccess: () => void;
}

export const useUserSettings = (): UseUserSettingsReturn => {
  // State for profile
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileUpdateSuccess, setProfileUpdateSuccess] = useState(false);
  
  // State for preferences
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [preferencesLoading, setPreferencesLoading] = useState(true);
  const [preferencesError, setPreferencesError] = useState<string | null>(null);
  const [preferencesUpdateSuccess, setPreferencesUpdateSuccess] = useState(false);
  
  // State for security actions
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState(false);
  const [twoFactorUpdateSuccess, setTwoFactorUpdateSuccess] = useState(false);

  // Load user profile
  const loadProfile = useCallback(async () => {
    try {
      setProfileLoading(true);
      setProfileError(null);
      
      const response = await userService.getProfile();
      if (response.success && response.data) {
        setProfile(response.data);
      } else {
        setProfileError('Erreur lors du chargement du profil');
      }
    } catch (err) {
      const errorMessage = err instanceof ApiError ? err.message : 'Erreur lors du chargement du profil';
      setProfileError(errorMessage);
      console.error('Error loading profile:', err);
    } finally {
      setProfileLoading(false);
    }
  }, []);

  // Load user preferences
  const loadPreferences = useCallback(async () => {
    try {
      setPreferencesLoading(true);
      setPreferencesError(null);
      
      const response = await userService.getPreferences();
      if (response.success && response.data) {
        setPreferences(response.data);
      } else {
        setPreferencesError('Erreur lors du chargement des préférences');
      }
    } catch (err) {
      const errorMessage = err instanceof ApiError ? err.message : 'Erreur lors du chargement des préférences';
      setPreferencesError(errorMessage);
      console.error('Error loading preferences:', err);
    } finally {
      setPreferencesLoading(false);
    }
  }, []);

  // Update profile
  const updateProfile = useCallback(async (data: UpdateProfileData) => {
    try {
      setProfileError(null);
      
      const response = await userService.updateProfile(data);
      if (response.success && response.data) {
        setProfile(response.data);
        setProfileUpdateSuccess(true);
        // Auto-clear success message after 3 seconds
        setTimeout(() => setProfileUpdateSuccess(false), 3000);
      } else {
        setProfileError('Erreur lors de la mise à jour du profil');
      }
    } catch (err) {
      const errorMessage = err instanceof ApiError ? err.message : 'Erreur lors de la mise à jour du profil';
      setProfileError(errorMessage);
      throw err;
    }
  }, []);

  // Change password
  const changePassword = useCallback(async (data: ChangePasswordData) => {
    try {
      setProfileError(null);
      
      const response = await userService.changePassword(data);
      if (response.success) {
        setPasswordChangeSuccess(true);
        // Auto-clear success message after 3 seconds
        setTimeout(() => setPasswordChangeSuccess(false), 3000);
      } else {
        setProfileError('Erreur lors du changement de mot de passe');
      }
    } catch (err) {
      const errorMessage = err instanceof ApiError ? err.message : 'Erreur lors du changement de mot de passe';
      setProfileError(errorMessage);
      throw err;
    }
  }, []);

  // Update preferences
  const updatePreferences = useCallback(async (newPreferences: Partial<UserPreferences>) => {
    try {
      setPreferencesError(null);
      
      const response = await userService.updatePreferences(newPreferences);
      if (response.success && response.data) {
        setPreferences(response.data);
        setPreferencesUpdateSuccess(true);
        // Auto-clear success message after 3 seconds
        setTimeout(() => setPreferencesUpdateSuccess(false), 3000);
      } else {
        setPreferencesError('Erreur lors de la mise à jour des préférences');
      }
    } catch (err) {
      const errorMessage = err instanceof ApiError ? err.message : 'Erreur lors de la mise à jour des préférences';
      setPreferencesError(errorMessage);
      throw err;
    }
  }, []);

  // Toggle two-factor authentication
  const toggleTwoFactor = useCallback(async (data: TwoFactorData) => {
    try {
      setProfileError(null);
      
      const response = await userService.toggleTwoFactor(data);
      if (response.success) {
        // Update local preferences to reflect 2FA status
        if (preferences) {
          setPreferences({ 
            ...preferences, 
            twoFactorEnabled: data.enabled 
          });
        }
        setTwoFactorUpdateSuccess(true);
        // Auto-clear success message after 3 seconds
        setTimeout(() => setTwoFactorUpdateSuccess(false), 3000);
      } else {
        setProfileError('Erreur lors de la configuration 2FA');
      }
    } catch (err) {
      const errorMessage = err instanceof ApiError ? err.message : 'Erreur lors de la configuration 2FA';
      setProfileError(errorMessage);
      throw err;
    }
  }, [preferences]);

  // Refresh functions
  const refreshProfile = useCallback(async () => {
    await loadProfile();
  }, [loadProfile]);

  const refreshPreferences = useCallback(async () => {
    await loadPreferences();
  }, [loadPreferences]);

  // Clear success states
  const clearProfileUpdateSuccess = useCallback(() => {
    setProfileUpdateSuccess(false);
  }, []);

  const clearPasswordChangeSuccess = useCallback(() => {
    setPasswordChangeSuccess(false);
  }, []);

  const clearPreferencesUpdateSuccess = useCallback(() => {
    setPreferencesUpdateSuccess(false);
  }, []);

  const clearTwoFactorUpdateSuccess = useCallback(() => {
    setTwoFactorUpdateSuccess(false);
  }, []);

  // Load data on mount
  useEffect(() => {
    loadProfile();
    loadPreferences();
  }, [loadProfile, loadPreferences]);

  return {
    // Data
    profile,
    preferences,
    
    // Loading states
    profileLoading,
    preferencesLoading,
    
    // Error states
    profileError,
    preferencesError,
    
    // Actions
    updateProfile,
    changePassword,
    updatePreferences,
    toggleTwoFactor,
    refreshProfile,
    refreshPreferences,
    
    // Success states
    profileUpdateSuccess,
    passwordChangeSuccess,
    preferencesUpdateSuccess,
    twoFactorUpdateSuccess,
    
    // Clear success states
    clearProfileUpdateSuccess,
    clearPasswordChangeSuccess,
    clearPreferencesUpdateSuccess,
    clearTwoFactorUpdateSuccess
  };
};